// =============================================================================
// MOP builder — shared types, fixed wording and seed content (T-MOP)
// =============================================================================
// One module owns the words, so the screen shows EXACTLY what the PDF prints.
//
// The fixed blocks below came out of the Beechmont pole-stand MOP (Rev 0 →
// Rev 0.1, 10 Sep 2026) and the scribe review of it. Two are load-bearing:
//
//  * CRANE_SETUP_WORDING — Rev 0 said, in Coretel's voice, that ground had
//    been assessed and timber packing would be used. Scribe: "Do not assume
//    or state ground conditions… Coretel is not assessing ground bearing
//    conditions… The crane company is responsible for its own crane setup
//    assessment." The corrected wording is FIXED here — a generated MOP can
//    add site notes but can never replace or soften this block.
//
//  * Lifting points are NEVER assumed (welded tabs / threaded rods beneath a
//    slip joint are NOT lifting points unless the documents say so) — that
//    lives in the pre-generation flags, not in template text.
// =============================================================================

export type MopRefKind = "lift_study" | "erection_drawings" | "layout_image" | "other";

export interface MopRef {
  path: string;               // storage path in hse-evidence, mops/<mop_id>/…
  file_name: string;
  description: string;
  kind: MopRefKind;
}

export interface MopMethodStep {
  hold?: boolean;             // 21 Sep 2026 — a HOLD POINT: the method stops here until the named person clears it
  title: string;              // "Pre-lift briefing"
  action: string;             // one-line what happens
  details: string;            // user's free text (field truth, any language)
  details_pro?: string;       // worker-professionalised version — NEVER adds facts
  image_path?: string | null; // optional snip, hse-evidence path
}

export interface MopInputs {
  meta: {
    mop_date: string;         // yyyy-mm-dd (local day key)
    client_chain: string;     // e.g. "NBN Co → Ventia → Coretel"
    crew: string[];           // names/roles, free text lines
  };
  crane: {
    crane_config_id: string | null;
    // Snapshot of the config's display fields at selection time — a later
    // library edit must not silently rewrite an approved MOP.
    model_label: string;      // "Liebherr LTM 1120-4.2 (hired as 100T)"
    config_label: string;
    counterweight_t: number | null;
    outriggers: string;       // "7.00 × 7.50 m"
    chart_wind_ms: number | null;
    chart_basis: string;      // machine | generic
  } | null;
  plant: { item: string; details: string }[];
  refs: MopRef[];
  scope: string;
  painted_structure: boolean; // soft-slings-only block on when true
  suspended_loads: boolean;   // WUSL controls block (default ON for crane MOPs)
  site_conditions_extra: string;  // ADDS to the fixed wording, never replaces it
  environmental: { wind_limit_ms: number | null; notes: string };
  method: MopMethodStep[];
  // 21 Sep 2026 — the standing "don'ts" for THIS job (Willowvale backbone,
  // Appendix B). Printed as their own block after the method. Optional so
  // MOPs saved before this field exists still load.
  donts?: string[];
  // 21 Sep 2026 — what is still unresolved on this MOP (from the backbone's
  // "still open" list, or typed). Prints as its own STILL OPEN panel so a
  // draft is at least as honest as the form that made it (Projects brief F).
  open_items?: string[];
  // 23 Sep 2026 (Projects brief 2, A2) — a DOCUMENT BLOCK: a titled section
  // that governs every step without being one. Willowvale's communications
  // protocol is the case that found it — call signs, mandatory read-back, a
  // reading reported after every adjustment, ALL STOP callable by anyone —
  // and several method steps say "per the call protocol", referring to
  // something that was not in the document the crew was holding, because the
  // builder had no home for it. Prints between the limits and the method, in
  // the order listed. Other uses: permit conditions, client-imposed
  // conditions, emergency arrangements, a hold-point summary.
  blocks?: { title: string; body: string }[];
  layout_image: string | null;    // hse-evidence path (the Google-Earth style overlay snip)
  forecast_image: string | null;  // hse-evidence path (weather forecast snip — prints in Environmental limits, Beechmont Rev 3 pattern)
  flags: {
    lifting_points_source: "" | "lift_study" | "erection_drawings" | "project_input";
    lifting_points_detail: string;
    acknowledged: string[];   // ids of non-blocking flags the manager has accepted
  };
}

// ---- Fixed wording — screen displays it read-only, PDF prints it ------------

export const CRANE_SETUP_WORDING =
  "Crane setup — including outrigger deployment, loading and any pad or packing " +
  "requirements — is per the attached lift study. The crane contractor is responsible " +
  "for its own crane setup assessment, including ground bearing suitability at the " +
  "configured outrigger loads. Coretel does not assess ground bearing conditions. " +
  "Any ground requirements stated in the lift study are carried through in this " +
  "document; where the lift study is silent, no ground condition is asserted here.";

export const SUSPENDED_LOADS_WORDING = [
  "No person positions any part of their body under a suspended load at any time.",
  "Hands off the load while it is suspended — alignment and control by taglines only.",
  "Any EWP works outside the lift path; it positions personnel only and never supports or steadies the load.",
  "An exclusion zone is established and enforced around the lift path before the load leaves the ground.",
  "Any person may call STOP by radio at any time; the lift does not resume until the lift supervisor clears it.",
];

// Coretel's OWN standing wind limit for work at height when there is no crane
// chart, engineer's limit or client limit to read from (James, 21 Sep 2026:
// "a base wind safety number … we can put in when blank").
//
// ** THIS IS A COMPANY RULE, NOT A STANDARD. ** Nothing in TIA-222, AS 3995 or
// the WHS regs was read to produce it, and the printed document says so in
// those words. Coretel has a hard rule against citing clauses nobody has read
// (a generated SWMS once carried an invented WHS reg while marked APPROVED),
// so this number is presented as ours and owned by a director.
//
// Change the figure here and every MOP that uses the button follows. A crane
// chart, a client limit or an engineer's limit always governs if lower — the
// wording below says that too.
export const CORETEL_STANDING_WIND_MS: number | null = 10;

export const CORETEL_STANDING_WIND_WORDING =
  `Wind limit ${CORETEL_STANDING_WIND_MS} m/s — Coretel's standing limit for work at height where no crane chart, ` +
  "engineer's limit or client limit applies. This is a Coretel rule, not a cited standard. A lower limit from a " +
  "crane chart, the client or the certifying engineer governs where one is stated.";

export const PAINTED_STRUCTURE_WORDING =
  "Painted structure — soft slings only. No chains, wire rope or hardware in direct " +
  "contact with painted surfaces at any point of rigging, lifting, tailing or landing.";

// ---- Seed method steps -------------------------------------------------------
// Pre-seeded on every new MOP (ticket: "base template pre-seeds the standard
// pre-lift steps which the user can edit/remove"). They are STARTING TEXT, not
// generated facts — the manager owns every word before generation.

// 21 Sep 2026 (James, Willowvale): "why does it say lift plans and cranes in
// a mast tension" — the crane seed was pre-loaded on EVERY new MOP, so a
// no-crane job started with "Crane setup per lift study" in its method. Now a
// new MOP seeds one generic pre-start step; choosing a crane in §2 adds the
// three crane steps; choosing "No crane" removes them again if untouched.
export const PRESTART_GENERIC = "All personnel briefed on this MOP and the SWMS; sign-on completed.";
export const PRESTART_CRANE   = "All personnel briefed on this MOP, the lift study and the SWMS; sign-on completed.";

export function seedMethodSteps(withCrane = false): MopMethodStep[] {
  const pre: MopMethodStep = { title: "Pre-start briefing and sign-on", action: withCrane ? PRESTART_CRANE : PRESTART_GENERIC, details: "" };
  return withCrane ? [pre, ...craneSeedSteps()] : [pre];
}

export function craneSeedSteps(): MopMethodStep[] {
  return [
    {
      title: "Establish exclusion zones",
      action: "Exclusion zone around the lift path set out and enforced before any rigging starts.",
      details: "",
    },
    {
      title: "Crane setup per lift study",
      action: "Crane contractor sets up per its lift study — configuration, counterweight and outriggers as specified. Coretel confirms the configuration matches the lift study before the first lift.",
      details: "",
    },
    {
      title: "Inspect lifting gear",
      action: "All slings, shackles and taglines inspected and tags current before use; gear matched to the lift study's rigging arrangement.",
      details: "",
    },
  ];
}

// True when a step is still exactly a crane seed step (nothing typed into it).
export function isUntouchedCraneSeed(s: MopMethodStep): boolean {
  return craneSeedSteps().some(c => c.title === s.title && c.action === s.action) && !s.details.trim() && !s.image_path;
}

export function emptyMopInputs(today: string): MopInputs {
  return {
    meta: { mop_date: today, client_chain: "", crew: [] },
    crane: null,
    plant: [],
    refs: [],
    scope: "",
    painted_structure: false,
    suspended_loads: false,      // switched on when a crane is picked (21 Sep 2026)
    site_conditions_extra: "",
    environmental: { wind_limit_ms: null, notes: "" },
    method: seedMethodSteps(),
    donts: [],
    open_items: [],
    blocks: [],
    layout_image: null,
    forecast_image: null,
    flags: { lifting_points_source: "", lifting_points_detail: "", acknowledged: [] },
  };
}

// ---- Pre-generation flags ----------------------------------------------------
// Every unresolved thing the scribe would have marked [VERIFY] surfaces HERE,
// before the PDF exists — never as a note inside the document. `blocking`
// flags stop generation outright; the rest need an explicit acknowledgement.

export interface MopFlag {
  id: string;
  blocking: boolean;
  text: string;
}

export function computeFlags(inputs: MopInputs): MopFlag[] {
  const flags: MopFlag[] = [];
  const isCraneMop = !!inputs.crane;

  if (isCraneMop && !inputs.refs.some(r => r.kind === "lift_study")) {
    flags.push({
      id: "lift_study_missing", blocking: true,
      text: "No lift study attached. A crane MOP cannot generate without the crane company's lift study — attach it under References.",
    });
  }
  if (isCraneMop && !inputs.flags.lifting_points_source) {
    flags.push({
      id: "lifting_points_unset", blocking: true,
      text: "Lifting points source not set. Say where the lifting method comes from (lift study / erection drawings / project input) — it is never assumed.",
    });
  }
  if (isCraneMop && inputs.crane?.chart_basis === "generic") {
    flags.push({
      id: "generic_chart", blocking: false,
      text: "The selected crane configuration holds a GENERIC chart, not the hired machine's own chart. Fine for planning — get the machine chart before anyone lifts.",
    });
  }
  if (!inputs.scope.trim()) {
    flags.push({ id: "scope_empty", blocking: true, text: "Scope is empty." });
  }
  if (inputs.method.length === 0) {
    flags.push({ id: "no_steps", blocking: true, text: "The method has no steps." });
  }
  inputs.method.forEach((s, i) => {
    if (!s.action.trim() && !s.details.trim()) {
      flags.push({
        id: `step_empty_${i}`, blocking: false,
        text: `Step ${i + 1} ("${s.title || "untitled"}") has no action or details.`,
      });
    }
  });
  const openItems = (inputs.open_items ?? []).map(s => s.trim()).filter(Boolean);
  if (openItems.length) {
    flags.push({
      id: "open_items", blocking: false,
      text: `${openItems.length} item${openItems.length === 1 ? "" : "s"} still open on this MOP — they print in the STILL OPEN panel. Close them or acknowledge that the document goes out with them listed.`,
    });
  }
  if (!inputs.environmental.wind_limit_ms) {
    flags.push({
      id: "no_wind_limit", blocking: false,
      text: isCraneMop
        ? "No wind limit set. Pull it from the lift study or the crane chart — the document will print the environmental block without a number."
        : "No wind limit set. Say what wind stops this work and where the figure came from — the document will print the environmental block without a number.",
    });
  }
  if (!inputs.layout_image) {
    flags.push({
      id: "no_layout", blocking: false,
      text: isCraneMop
        ? "No site layout image attached (laydown / lift path overlay)."
        : "No site layout image attached (site plan — anchors, access, work positions).",
    });
  }
  return flags;
}
